
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Chart from 'chart.js';

export interface AgingCohort {
  key: string;
  label: string;
}

export interface AgingRow {
  accountSfid: string;
  accountName: string;
  accountType: string;
  [cohortKey: string]: string | number;
}

// timeline: future (cool) -> overdue (warm, escalating to deep red)
export const COHORT_COLORS: Record<string, string> = {
  notInvoicedRecent: '#9fa8da',
  notInvoicedStale: '#8e24aa',
  due30Plus: '#90a4ae',
  due0_30: '#4db6ac',
  d0_30: '#fbc02d',
  d31_60: '#fb8c00',
  d61_90: '#f4511e',
  d90Plus: '#e53935',
};

const eur = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

@Component
export default class AgingChart extends Vue {
  @Prop({ required: true }) rows!: AgingRow[];
  @Prop({ required: true }) cohorts!: AgingCohort[];
  // tooltip footer label for the stacked sum; defaults to "Open total"
  @Prop({ default: '' }) totalLabel!: string;

  private chart: Chart | null = null;

  mounted() {
    this.draw();
  }

  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  @Watch('rows')
  @Watch('cohorts')
  onDataChange() {
    this.draw();
  }

  private get labels(): string[] {
    return this.rows.map(
      (r) =>
        r.accountName ||
        r.accountSfid ||
        (r.accountNo as string) ||
        (this.$t('accounting.aging.no-account') as string),
    );
  }

  private get datasets(): Chart.ChartDataSets[] {
    return this.cohorts.map((c) => ({
      label: c.label,
      backgroundColor: COHORT_COLORS[c.key] || '#90a4ae',
      data: this.rows.map((r) => Number(r[c.key]) || 0),
    }));
  }

  private draw() {
    if (!this.$refs.canvas) {
      return;
    }
    const ctx = (this.$refs.canvas as HTMLCanvasElement).getContext('2d');
    if (!ctx) {
      return;
    }

    const data = { labels: this.labels, datasets: this.datasets };

    if (this.chart) {
      this.chart.data.labels = data.labels;
      this.chart.data.datasets = data.datasets;
      this.chart.update();
      return;
    }

    this.chart = new Chart(ctx, {
      type: 'horizontalBar',
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'top' },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: (item, chartData) => {
              const ds = chartData.datasets?.[item.datasetIndex ?? 0];
              const value = Number(item.xLabel) || 0;
              return `${ds?.label ?? ''}: ${eur.format(value)}`;
            },
            footer: (items) => {
              const total = items.reduce(
                (s, i) => s + (Number(i.xLabel) || 0),
                0,
              );
              const label =
                this.totalLabel ||
                (this.$te('accounting.aging.open-total')
                  ? this.$t('accounting.aging.open-total')
                  : 'Open total');
              return `${label}: ${eur.format(total)}`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              stacked: true,
              ticks: {
                callback: (value) => eur.format(Number(value)),
              },
            },
          ],
          yAxes: [{ stacked: true }],
        },
        onClick: (_evt, elements) => {
          if (elements && elements.length) {
            const index = (elements[0] as any)._index;
            const row = this.rows[index];
            if (row) {
              this.$emit('select-account', row);
            }
          }
        },
      },
    });
  }
}
